import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Clock, Calendar, Users, AlertTriangle, Loader2 } from 'lucide-react';

interface PeakLoadCapacityPlanningProps {
  startDate: string;
  endDate: string;
}

const PeakLoadCapacityPlanning: React.FC<PeakLoadCapacityPlanningProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    setTimeout(() => {
      const heatmapData = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => ({
        id: day,
        data: Array.from({ length: 24 }, (_, hour) => ({
          x: `${hour}:00`,
          y: Math.floor(20 + Math.random() * 80)
        }))
      }));

      setData({
        peak_hour: '14:00 - 15:00',
        peak_day: 'Wednesday',
        current_capacity: 10,
        recommended_capacity: 13,
        utilization_rate: 87,
        heatmapData
      });
      setLoading(false);
    }, 500);
  }, [startDate, endDate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <Alert>
        <AlertDescription>No capacity data available.</AlertDescription>
      </Alert>
    );
  }

  const gaugeOptions: ApexOptions = {
    chart: { type: 'radialBar' },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        dataLabels: {
          name: { fontSize: '16px', offsetY: 120 },
          value: { fontSize: '24px', formatter: (val) => `${val}%` }
        }
      }
    },
    fill: { colors: [data.utilization_rate > 85 ? '#f44336' : '#4caf50'] },
    labels: ['Utilization Rate']
  };

  return (
    <div className="space-y-6">
      {/* ── KPI stat strip ────────────────────────────────────────────────── */}
      <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border/60 bg-muted/10 overflow-hidden">

        {/* Peak Hour */}
        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[150px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />Peak Hour
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">{data.peak_hour}</span>
          <span className="text-[11px] text-muted-foreground mt-0.5">Busiest time of day</span>
        </div>

        {/* Peak Day */}
        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />Peak Day
          </span>
          <span className="text-lg font-semibold mt-1">{data.peak_day}</span>
          <span className="text-[11px] text-muted-foreground mt-0.5">Highest volume day</span>
        </div>

        {/* Current Capacity */}
        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[140px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" />Current Capacity
          </span>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-lg font-semibold tabular-nums">{data.current_capacity}</span>
            <span className="text-sm text-muted-foreground">agents</span>
          </div>
          <span className="text-[11px] text-muted-foreground mt-0.5">Active support agents</span>
        </div>

        {/* Recommended Capacity */}
        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[160px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5" />Recommended Capacity
          </span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-lg font-semibold tabular-nums">{data.recommended_capacity}</span>
            <span className="text-sm text-muted-foreground">agents</span>
          </div>
          {data.recommended_capacity > data.current_capacity ? (
            <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400 mt-0.5">
              +{data.recommended_capacity - data.current_capacity} agents needed
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground mt-0.5">Staffing on target</span>
          )}
        </div>

      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-5">
        {/* Utilization Gauge */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Current Utilization</CardTitle>
          </CardHeader>
          <CardContent>
            <Chart
              options={gaugeOptions}
              series={[data.utilization_rate]}
              type="radialBar"
              height={300}
            />
            <Alert variant={data.utilization_rate > 85 ? 'destructive' : 'default'} className="mt-2">
              <AlertDescription>
                {data.utilization_rate > 85 ? 'Over-utilized! Add more agents.' : 'Healthy utilization rate.'}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Heatmap */}
        <Card className="md:col-span-3">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Conversation Volume Heatmap</CardTitle>
            <CardDescription>Volume by day and hour of week</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: 350 }}>
              <ResponsiveHeatMap
                data={data.heatmapData}
                margin={{ top: 60, right: 90, bottom: 60, left: 90 }}
                valueFormat=">-.0f"
                axisTop={{ tickSize: 5, tickPadding: 5, tickRotation: -90 }}
                axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
                colors={{
                  type: 'diverging',
                  scheme: 'red_yellow_green',
                  divergeAt: 0.5,
                  minValue: 0,
                  maxValue: 100
                }}
                emptyColor="#555555"
                legends={[
                  {
                    anchor: 'bottom',
                    translateX: 0,
                    translateY: 30,
                    length: 400,
                    thickness: 8,
                    direction: 'row',
                    tickPosition: 'after',
                    tickSize: 3,
                    tickSpacing: 4,
                    tickOverlap: false,
                    title: 'Conversation Volume →',
                    titleAlign: 'start',
                    titleOffset: 4
                  }
                ]}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Capacity Planning Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-4 space-y-1">
            <li>
              <span className="text-xs text-muted-foreground">
                Schedule {data.recommended_capacity - data.current_capacity} additional agents during peak hours
              </span>
            </li>
            <li>
              <span className="text-xs text-muted-foreground">
                Consider flex/part-time staff for {data.peak_day}s
              </span>
            </li>
            <li>
              <span className="text-xs text-muted-foreground">
                Target 75–85% utilization for optimal service levels
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default PeakLoadCapacityPlanning;
